import React, { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/LanguageContext';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';

/**
 * Temporary test page to verify i18n functionality
 * Access at /i18n-test (add route manually or navigate programmatically)
 */
const I18nTestPage = () => {
  const { language, setLanguage, t, formatDate, formatCurrency, formatNumber } = useI18n();
  const [testResults, setTestResults] = useState<Record<string, any>>({});
  const [htmlLang, setHtmlLang] = useState('');

  useEffect(() => {
    // Test HTML lang attribute
    setHtmlLang(document.documentElement.lang);
    const observer = new MutationObserver(() => {
      setHtmlLang(document.documentElement.lang);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });

    // Run all tests
    runTests();

    return () => observer.disconnect();
  }, [language, t, formatDate, formatCurrency, formatNumber]);

  const runTests = () => {
    const results: Record<string, any> = {};

    // Test 1: HTML lang attribute
    results.htmlLang = {
      expected: language,
      actual: document.documentElement.lang,
      pass: document.documentElement.lang === language,
    };

    // Test 2: Fallback chain (try a key that exists in EN but we'll temporarily remove from LV)
    const fallbackKey = 'addCost.title'; // This should exist in both
    results.fallback = {
      key: fallbackKey,
      value: t(fallbackKey),
      hasValue: !!t(fallbackKey) && t(fallbackKey) !== fallbackKey,
    };

    // Test 3: ICU Plural support
    const pluralTests = [
      { count: 1, key: 'home.issuesCount' },
      { count: 2, key: 'home.issuesCount' },
      { count: 5, key: 'home.issuesCount' },
    ];
    results.plurals = pluralTests.map(test => ({
      count: test.count,
      key: test.key,
      result: t(test.key, { count: test.count }),
      // For LV: 1=one, 2-9=few, 10+=other
      // For EN: 1=one, other
    }));

    // Test 4: Currency formatting
    const testAmount = 1234.56;
    results.currency = {
      en: formatCurrency(testAmount),
      lv: language === 'lv' ? formatCurrency(testAmount) : 'Switch to LV to test',
      note: 'LV should use space as thousand separator, comma as decimal',
    };

    // Test 5: Date formatting
    const testDate = new Date('2024-01-15');
    results.date = {
      en: formatDate(testDate, 'dd-MMM-yyyy'),
      lv: language === 'lv' ? formatDate(testDate, 'dd-MMM-yyyy') : 'Switch to LV to test',
      note: 'LV should show Latvian month name (janvāris)',
    };

    // Test 6: Number formatting
    const testNumber = 1234567.89;
    results.number = {
      en: formatNumber(testNumber),
      lv: language === 'lv' ? formatNumber(testNumber) : 'Switch to LV to test',
      note: 'LV uses different separators',
    };

    // Test 7: Category translation
    results.categoryTranslation = {
      refueling: t('category.Refueling'),
      insurance: t('category.Insurance'),
      maintenance: t('category.Maintenance'),
    };

    setTestResults(results);

    // Log to console for easy inspection
    console.group('🧪 i18n Test Results');
    console.log('Language:', language);
    console.log('HTML lang:', document.documentElement.lang);
    console.log('Results:', results);
    console.groupEnd();
  };

  return (
    <PageLayout title="i18n Test Page">
      <div className="space-y-6">
        {/* Language Toggle */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-3">Language Toggle</h2>
          <div className="flex gap-2">
            <Button
              onClick={() => setLanguage('en')}
              variant={language === 'en' ? 'default' : 'outline'}
            >
              English
            </Button>
            <Button
              onClick={() => setLanguage('lv')}
              variant={language === 'lv' ? 'default' : 'outline'}
            >
              Latvian
            </Button>
          </div>
          <p className="text-sm text-gray-600 mt-2">Current: {language}</p>
        </div>

        {/* Test 1: HTML lang */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 1: HTML lang Attribute</h2>
          <div className={`p-3 rounded ${testResults.htmlLang?.pass ? 'bg-green-50' : 'bg-red-50'}`}>
            <p className="text-sm">
              Expected: <code>{testResults.htmlLang?.expected}</code>
            </p>
            <p className="text-sm">
              Actual: <code>{htmlLang}</code>
            </p>
            <p className="text-sm font-semibold">
              Status: {testResults.htmlLang?.pass ? '✅ PASS' : '❌ FAIL'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Check DevTools → Elements → &lt;html lang="{htmlLang}"&gt;
            </p>
          </div>
        </div>

        {/* Test 2: Fallback */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 2: Fallback Chain</h2>
          <p className="text-sm mb-2">Key: <code>{testResults.fallback?.key}</code></p>
          <p className="text-sm mb-2">Value: <code>{testResults.fallback?.value}</code></p>
          <p className="text-xs text-gray-500">
            To test: Temporarily remove this key from lv.ts → should show EN version
          </p>
        </div>

        {/* Test 3: Plurals */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 3: ICU Plural Support</h2>
          {testResults.plurals?.map((test: any, idx: number) => (
            <div key={idx} className="mb-2 p-2 bg-gray-50 rounded">
              <p className="text-sm">
                count={test.count}: <code>{test.result}</code>
              </p>
              {language === 'lv' && (
                <p className="text-xs text-gray-500">
                  Expected: {test.count === 1 ? 'one' : test.count >= 2 && test.count <= 9 ? 'few' : 'other'}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Test 4: Currency */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 4: Currency Formatting</h2>
          <p className="text-sm">Amount: 1234.56 EUR</p>
          <p className="text-sm">EN: <code>{testResults.currency?.en}</code></p>
          <p className="text-sm">LV: <code>{testResults.currency?.lv}</code></p>
          <p className="text-xs text-gray-500">{testResults.currency?.note}</p>
        </div>

        {/* Test 5: Date */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 5: Date Formatting</h2>
          <p className="text-sm">Date: 2024-01-15</p>
          <p className="text-sm">EN: <code>{testResults.date?.en}</code></p>
          <p className="text-sm">LV: <code>{testResults.date?.lv}</code></p>
          <p className="text-xs text-gray-500">{testResults.date?.note}</p>
        </div>

        {/* Test 6: Number */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 6: Number Formatting</h2>
          <p className="text-sm">Number: 1234567.89</p>
          <p className="text-sm">EN: <code>{testResults.number?.en}</code></p>
          <p className="text-sm">LV: <code>{testResults.number?.lv}</code></p>
        </div>

        {/* Test 7: Category Translation */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-lg font-semibold mb-2">Test 7: Category Translations</h2>
          <div className="space-y-1">
            <p className="text-sm">Refueling: <code>{testResults.categoryTranslation?.refueling}</code></p>
            <p className="text-sm">Insurance: <code>{testResults.categoryTranslation?.insurance}</code></p>
            <p className="text-sm">Maintenance: <code>{testResults.categoryTranslation?.maintenance}</code></p>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Test search in AddCostPage category modal with these terms
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h2 className="text-lg font-semibold mb-2">Manual Testing Instructions</h2>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Toggle EN/LV → Check &lt;html lang&gt; updates (no flash)</li>
            <li>Check console for test results</li>
            <li>Go to AddCostPage → Test category search in LV</li>
            <li>Temporarily remove a key from lv.ts → Should fallback to EN</li>
          </ol>
        </div>
      </div>
    </PageLayout>
  );
};

export default I18nTestPage;


